/**
 * AIInput — Text + voice input for the AI Assistant
 *
 * Features:
 *  - Auto-resizing textarea (1-4 lines), hidden scrollbar
 *  - Enter to send, Shift+Enter for newline
 *  - Mic button: ChatGPT-style continuous recording
 *  - When recording, voice UI replaces the input bar
 *  - On confirm, populates textarea with transcript + shows send
 *  - Gradient send button with animated icon
 *  - Clean, modern styling with dark mode
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';
import VoiceInput from './VoiceInput';
import '../../assets/styles/widgets.css';

import { toast } from 'sonner';

const MAX_INPUT_LENGTH = 2000;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB per file
const MAX_FILES_PER_MESSAGE = 5;

export default function AIInput({ onSend, onStop, isLoading = false, isStreaming = false, placeholder = 'Ask Lina anything…', voiceLocale = 'en-US' }) {
  const [text, setText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [inputSource, setInputSource] = useState('text');
  const [pendingFiles, setPendingFiles] = useState([]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const disabled = isLoading || isStreaming;
  const hasText = text.trim().length > 0;
  const hasFiles = pendingFiles.length > 0;
  // Don't allow send while recording; interim text alone can't be sent
  const canSend = !isRecording && (hasText || hasFiles);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    }
  }, [text, interimText, isRecording]);

  // Focus textarea after voice recording ends
  useEffect(() => {
    if (!isRecording && textareaRef.current && !disabled && hasText) {
      textareaRef.current.focus();
    }
  }, [isRecording, disabled, hasText]);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if ((!trimmed && pendingFiles.length === 0) || disabled) return;
    const messageText = trimmed || (pendingFiles.length > 0 ? `I'm attaching ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}.` : '');
    onSend(messageText, { inputType: inputSource, files: pendingFiles });
    setText('');
    setPendingFiles([]);
    setInputSource('text');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [text, disabled, onSend, inputSource, pendingFiles]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = MAX_FILES_PER_MESSAGE - pendingFiles.length;
    if (remainingSlots <= 0) {
      toast.error(`You can attach up to ${MAX_FILES_PER_MESSAGE} files per message.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const valid = [];
    let droppedForCap = 0;
    for (const f of files) {
      if (valid.length >= remainingSlots) {
        droppedForCap++;
        continue;
      }
      if (!f.type) {
        toast.error(`"${f.name}" — file type couldn't be detected. Try renaming with a proper extension.`);
        continue;
      }
      if (f.size === 0) {
        toast.error(`"${f.name}" is empty.`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`"${f.name}" exceeds the ${MAX_FILE_SIZE / (1024 * 1024)} MB per-file limit.`);
        continue;
      }
      valid.push(f);
    }
    if (droppedForCap > 0) {
      toast.error(`Only ${MAX_FILES_PER_MESSAGE} files allowed per message — ${droppedForCap} skipped.`);
    }
    if (valid.length > 0) setPendingFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleChange = (e) => {
    if (e.target.value.length <= MAX_INPUT_LENGTH) {
      setText(e.target.value);
      setInputSource('text');
    }
  };

  // Voice transcript confirmed (auto-stop after silence or manual stop)
  const handleVoiceTranscript = useCallback((voiceText) => {
    if (!voiceText?.trim() || disabled) return;
    setText(voiceText.trim());
    setInterimText('');
    setInputSource('voice');
    setIsRecording(false);
  }, [disabled]);

  const handleVoiceCancel = useCallback(() => {
    setInterimText('');
    setIsRecording(false);
  }, []);

  // Live transcript preview while recording
  const handleInterim = useCallback((liveText) => {
    setInterimText(liveText);
  }, []);

  const handleListeningChange = useCallback((listening) => {
    setIsRecording(listening);
    if (!listening) {
      setInterimText('');
      setIsVoiceActive(false);
    }
  }, []);

  const handleVoiceActivity = useCallback((active) => {
    setIsVoiceActive(active);
  }, []);

  // Value shown in the textarea: interim live text while recording, actual text otherwise
  const textareaDisplayValue = isRecording ? interimText : text;

  return (
    <div className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 px-3 py-2.5 flex-shrink-0">
      {/* Pending file chips */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {pendingFiles.map((file, idx) => (
            <div
              key={`${file.name}-${idx}`}
              className="flex items-center gap-2 pl-2 pr-1 py-1 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded-lg text-xs text-indigo-700 dark:text-indigo-300 max-w-[220px]"
              title={`${file.name} · ${(file.size / 1024).toFixed(0)} KB`}
            >
              <PaperClipIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveFile(idx)}
                disabled={disabled}
                className="flex-shrink-0 p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
                aria-label={`Remove ${file.name}`}
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={cn(
        'ai-chat-container flex items-end gap-1.5 border bg-gray-50 dark:bg-gray-900',
        'border-gray-200 dark:border-gray-800',
        'transition-all duration-200 px-3 py-1 rounded-2xl',
        isRecording && !isVoiceActive && 'voice-border-idle',
        isRecording && isVoiceActive && 'voice-border-active'
      )}>
        {/* Attach files button — left side */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isRecording}
          title="Attach files"
          aria-label="Attach files"
          className={cn(
            'flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center self-end mb-0.5',
            'text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400',
            'hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors',
            'disabled:opacity-40 disabled:cursor-not-allowed'
          )}
        >
          <PaperClipIcon className="w-4 h-4" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Textarea — shows live interim text while recording */}
        <textarea
          ref={textareaRef}
          value={textareaDisplayValue}
          onChange={isRecording ? undefined : handleChange}
          onKeyDown={isRecording ? undefined : handleKeyDown}
          readOnly={isRecording}
          placeholder={isRecording ? 'Listening…' : placeholder}
          aria-label="Message Tasklina AI"
          disabled={disabled}
          rows={1}
          className={cn(
            'ai-chat-input flex-1 resize-none bg-transparent border-0',
            'text-gray-900 dark:text-gray-100 text-sm leading-relaxed',
            'py-1.5 px-1',
            'placeholder:text-gray-400 dark:placeholder:text-gray-500',
            'focus:outline-none focus:ring-0 outline-none',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'overflow-y-auto scrollbar-none',
            '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
            isRecording && 'text-gray-400 dark:text-gray-500 italic cursor-default select-none'
          )}
        />

        {/* Mic button — right side, always visible */}
        <div className="flex-shrink-0 self-end mb-0.5">
          <VoiceInput
            onTranscript={handleVoiceTranscript}
            onCancel={handleVoiceCancel}
            onListeningChange={handleListeningChange}
            onVoiceActivity={handleVoiceActivity}
            onInterim={handleInterim}
            locale={voiceLocale}
            disabled={disabled}
            silenceTimeoutMs={4000}
          />
        </div>

        {/* Send / Stop button — far right */}
        {isLoading || isStreaming ? (
          <button
            onClick={onStop}
            className={cn(
              'flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center self-end mb-0.5',
              'transition-all duration-200',
              'bg-gray-700 dark:bg-gray-600 text-white hover:bg-gray-800 dark:hover:bg-gray-500',
              'shadow-sm hover:shadow-md active:scale-95'
            )}
            title="Stop generating"
            aria-label="Stop generating"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send message"
            className={cn(
              'flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center self-end mb-0.5',
              'transition-all duration-200',
              canSend
                ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-500/25 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105 active:scale-95'
                : 'bg-gray-200 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            )}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
